"use client"

import React from 'react';
import dynamic from 'next/dynamic';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ComponentRenderer from './ComponentRenderer';
import type { ComponentNode } from '../types/template';

const Slider = dynamic(() => import('react-slick'), { ssr: false });

function PrevArrow(props: any) {
    const { onClick } = props;
    return (
        <button
            style={{
                position: 'absolute',
                left: '-10px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(148,163,184,0.3)',
                color: '#334155',
                boxShadow: '0 4px 12px rgba(15,23,42,0.12)',
                cursor: 'pointer',
                zIndex: 100,
                fontSize: '20px',
                padding: 0,
            }}
            onClick={onClick}
            aria-label="Previous slide"
            type="button"
        >
            &#10094;
        </button>
    );
}

function NextArrow(props: any) {
    const { onClick } = props;
    return (
        <button
            style={{
                position: 'absolute',
                right: '-10px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(148,163,184,0.3)',
                color: '#334155',
                boxShadow: '0 4px 12px rgba(15,23,42,0.12)',
                cursor: 'pointer',
                zIndex: 100,
                fontSize: '20px',
                padding: 0,
            }}
            onClick={onClick}
            aria-label="Next slide"
            type="button"
        >
            &#10095;
        </button>
    );
}

interface MerchantSectionSliderProps {
    items: any[];
    cardComponent: ComponentNode;
    context?: {
        companyId?: string;
        templateId?: string;
        domain?: string;
        pageSlug?: string;
        store_slug?: string;
        slug_type?: string;
    };
    slideInterval?: number;
    autoPlay?: boolean;
    totalCards?: number;
    // Override for full-width carousels (e.g. hero banners) that should show exactly
    // one slide at a time instead of the default "up to 3 side by side" merchant-grid look.
    slidesToShow?: number;
    slideHeight?: number;
    containerClassName?: string;
}

const MerchantSectionSlider: React.FC<MerchantSectionSliderProps> = ({
    items,
    cardComponent,
    context,
    totalCards,
    autoPlay = false,
    slideInterval = 4000,
    slidesToShow: slidesToShowOverride,
    slideHeight = 300,
    containerClassName,
}) => {
    const slides = totalCards ? items.slice(0, totalCards) : items;

    if (!slides.length) {
        return null;
    }

    const slidesToShow = slidesToShowOverride ?? Math.min(3, slides.length);
    const settings = {
        dots: true,
        arrows: true,
        infinite: slides.length > slidesToShow,
        autoplay: autoPlay && slides.length > 1,
        autoplaySpeed: slideInterval,
        pauseOnHover: true,
        speed: 600,
        slidesToShow,
        slidesToScroll: 1,
        adaptiveHeight: false,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: Math.min(2, slidesToShow, slides.length),
                    slidesToScroll: 1,
                    adaptiveHeight: false,
                },
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    adaptiveHeight: false,
                },
            },
        ],
    };

    return (
        <>
            <style>{`
                .merchant-slider-container {
                    overflow: visible !important;
                }
                .merchant-slider-container .slick-track {
                    display: flex !important;
                }
                .merchant-slider-container .slick-slide {
                    overflow: visible !important;
                    height: ${slideHeight}px !important;
                    display: flex !important;
                    align-items: stretch !important;
                }
                .merchant-slider-container .slick-slide > div {
                    display: flex !important;
                    flex: 1 !important;
                    height: 100% !important;
                }
                .merchant-slider-container .slick-dots {
                    bottom: -40px !important;
                }
                .merchant-slider-container .slick-dots li button:before {
                    color: #f97316 !important;
                    font-size: 8px !important;
                }
                .merchant-slider-container .slick-dots li.slick-active button:before {
                    color: #f97316 !important;
                }
            `}</style>
            <div className={['merchant-slider-container', containerClassName].filter(Boolean).join(' ')} style={{ width: '100%', paddingBottom: '60px', position: 'relative', overflow: 'visible' }}>
                <Slider {...settings}>
                    {slides.map((item, index) => (
                        <div key={index} style={{ padding: '0 12px', boxSizing: 'border-box', height: `${slideHeight}px`, display: 'flex', overflow: 'visible' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', height: '100%', width: '100%', overflow: 'visible' }}>
                                <ComponentRenderer
                                    component={cardComponent}
                                    data={item}
                                    context={context}
                                />
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>
        </>
    );
};

export default MerchantSectionSlider;
